"use client";

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CitizensAPI } from "@/lib/api";
import { toast } from "sonner";
import { Citizen } from "@/types";
import { CitizenSearch } from "@/components/citizens/CitizenSearch";
import { CitizenList } from "@/components/citizens/CitizenList";

export default function CitizensPage() {
  const [citizens, setCitizens] = useState<Citizen[]>([]);
  const [totalCitizens, setTotalCitizens] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Default to 50 rows per page
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderBy, setOrderBy] = useState("modified_date");
  const [isVerified, setIsVerified] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function fetchData() {
      if (ignore) return;
      setIsLoading(true);
      try {
        // Build query parameters
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("limit", pageSize.toString());

        if (searchQuery) {
          params.append("searchTerm", searchQuery);
        }

        if (orderBy) {
          params.append("order_by", orderBy);
        }

        if (isVerified !== null) {
          params.append("is_verified", isVerified);
        }

        if (isActive !== null) {
          params.append("is_active", isActive);
        }

        const response = await CitizensAPI.getAll(
          page,
          pageSize,
          searchQuery,
          controller.signal,
          orderBy,
          isVerified,
          isActive
        );

        if (!ignore) {
          setCitizens(response.data || []);
          setTotalCitizens(response.pagination?.total || 0);
        }
      } catch (error) {
        if (!ignore && error.name !== "AbortError") {
          console.error("Error fetching citizens:", error);
          toast.error("Failed to load citizens");
          setCitizens([]);
          setTotalCitizens(0);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [page, pageSize, searchQuery, orderBy, isVerified, isActive]);

  const handleSearch = (query: string) => {
    setPage(1);
    setSearchQuery(query);
  };

  const handleOrderChange = (value: string) => {
    setPage(1);
    setOrderBy(value);
  };

  const handleVerifiedChange = (value: string | null) => {
    setPage(1);
    setIsVerified(value);
  };

  const handleActiveChange = (value: string | null) => {
    setPage(1);
    setIsActive(value);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleResetFilters = () => {
    setOrderBy("modified_date");
    setIsVerified(null);
    setIsActive(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Citizens</h1>
          <p className="text-muted-foreground">
            Manage citizen profiles in the system
          </p>
        </div>
        <Button asChild>
          <Link to="/citizens/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Citizen
          </Link>
        </Button>
      </div>

      <CitizenSearch
        initialQuery={searchQuery}
        onSearch={handleSearch}
        orderBy={orderBy}
        onOrderChange={handleOrderChange}
        isVerified={isVerified}
        onVerifiedChange={handleVerifiedChange}
        isActive={isActive}
        onActiveChange={handleActiveChange}
        onResetFilters={handleResetFilters}
      />

      <CitizenList
        citizens={citizens || []}
        isLoading={isLoading}
        totalCount={totalCitizens}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
